import cx from 'classnames';
import { FC, useEffect, useId, useState } from 'react';

import { SelectWithController } from 'src/components/Inputs';

import { countriesData } from 'src/store/countries';
import { statesData } from 'src/store/states';
import { gdprFieldsData } from '.';

import { RenderProps } from '../MarketoForm/MarketoForm';

// helpers
import { getUserTimezone } from 'src/lib/browser-utils';
import { getRequiredMessage } from 'src/lib/utils';

interface Props extends RenderProps {
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  controlClassName?: string;
  consentCheckboxClassName?: string;
  excludeActiveCountries?: boolean;
  isDarkMode?: boolean;
  defaultCountryFromTimeZone?: boolean;
  includeState?: boolean;
  /** renders each select's label as its placeholder, for in-field label designs */
  usePlaceholderLabels?: boolean;
}

const GdprFields: FC<Props> = ({
  className,
  inputClassName,
  labelClassName,
  controlClassName,
  consentCheckboxClassName,
  control,
  excludeActiveCountries = false,
  errors,
  includeState = true,
  isDarkMode = false,
  register,
  setValue,
  defaultCountryFromTimeZone = false,
  usePlaceholderLabels = false,
}) => {
  const [userCountry, setUserCountry] = useState<{
    requiresConsent: boolean;
    label: string;
    value: string;
  } | null>(null);

  useEffect(() => {
    // sends the consent value to react-hook-form for hidden field
    if (typeof setValue === 'function') {
      setValue(
        'Is_Request_in_EEA_or_Unknown__c',
        userCountry?.requiresConsent ?? false,
      );
    }
  }, [userCountry, setValue]);

  if (excludeActiveCountries) {
    Object.keys(countriesData).forEach((key) => {
      return countriesData[key].isActive && delete countriesData[key];
    });
  }

  useEffect(() => {
    if (!defaultCountryFromTimeZone) {
      return;
    }

    const userTimezone = getUserTimezone();
    if (!userTimezone) {
      return;
    }

    const match = countriesData.find((countryData) => {
      return countryData?.timezones.includes(userTimezone);
    });
    if (!match) {
      return;
    }

    // Set local state
    setUserCountry({
      label: match.label,
      value: match.value,
      requiresConsent: match.requiresConsent ?? false,
    });

    // Propagate up to form state
    if (typeof setValue === 'function') {
      setValue(gdprFieldsData.country.name, match.value);
    }
  }, [defaultCountryFromTimeZone, setValue]);

  const isConsentCountry = userCountry?.requiresConsent === true;
  const shouldShowStateDropdown =
    includeState && userCountry?.value === 'United States';
  const optInId = `Marketing_Comms_Opt_in__c-${useId()}`;

  return (
    <>
      <SelectWithController
        className={className}
        inputClassName={inputClassName}
        labelClassName={labelClassName}
        controlClassName={controlClassName}
        label={gdprFieldsData.country.label}
        placeholder={
          usePlaceholderLabels ? gdprFieldsData.country.label : undefined
        }
        name={gdprFieldsData.country.name}
        describedby={gdprFieldsData.country.describedby}
        isDarkMode={isDarkMode}
        id={gdprFieldsData.country.name}
        register={register}
        required={getRequiredMessage(gdprFieldsData.country.label)}
        errorMessage={errors?.[gdprFieldsData.country.name]?.['message']}
        setValue={setValue}
        control={control}
        options={countriesData}
        onChange={setUserCountry}
        autoComplete={gdprFieldsData.country.autoComplete}
        controlledValue={userCountry}
      />

      {shouldShowStateDropdown && (
        <SelectWithController
          className={className}
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          controlClassName={controlClassName}
          label={gdprFieldsData.state.label}
          placeholder={
            usePlaceholderLabels ? gdprFieldsData.state.label : undefined
          }
          name={gdprFieldsData.state.name}
          describedby={gdprFieldsData.state.describedby}
          isDarkMode={isDarkMode}
          id={gdprFieldsData.state.name}
          register={register}
          required={getRequiredMessage(gdprFieldsData.state.label)}
          errorMessage={errors?.[gdprFieldsData.state.name]?.['message']}
          setValue={setValue}
          control={control}
          options={statesData}
          autoComplete={gdprFieldsData.state.autoComplete}
        />
      )}

      <input
        name='Is_Request_in_EEA_or_Unknown__c'
        type='hidden'
        value={isConsentCountry.toString()}
        {...register('Is_Request_in_EEA_or_Unknown__c')}
      />

      {/*
        TODO implement threads checkbox to replace this existing component
        [redacted]#############################
      */}

      {isConsentCountry && (
        <div className={cx('grid-container full', consentCheckboxClassName)}>
          <div className='grid-x form-group'>
            <div className='checkbox__container'>
              <label className='cta-section__label checkbox--label-inline checkbox'>
                <div className='checkbox__input-container'>
                  <input
                    className='checkbox__input'
                    type='checkbox'
                    id={optInId}
                    name='Marketing_Comms_Opt_in__c'
                    {...register('Marketing_Comms_Opt_in__c')}
                  />
                  <div
                    className={cx('checkbox__faux-input', {
                      'checkbox__faux-input--dark background background--color-black1000':
                        isDarkMode,
                    })}
                  ></div>
                </div>
              </label>
              <label
                htmlFor={optInId}
                className='cta-section__label checkbox__label-text checkbox__label-text--inline-block'
                dangerouslySetInnerHTML={{ __html: gdprFieldsData.optInLabel }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

GdprFields.displayName = 'GdprFields';
export default GdprFields;
